import { MutationOptions, QueryOptions, unwrap } from './common';
import { RunningAny, RunningProject } from '~/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useRunningAnys } from './JobApi';
import { isRunningProject } from '~/utils/runningAny';

export function useRunningProject({
  projectId,
  ...options
}: Omit<QueryOptions<RunningAny[], RunningProject | undefined>, 'select'> & {
  employeeId: number;
  projectId: number;
}) {
  return useRunningAnys({
    ...options,
    select: (data) =>
      data.find(
        (ra) => isRunningProject(ra) && Number(ra.project.id) === projectId,
      ) as RunningProject | undefined,
  });
}

export function useStartProject(
  options?: MutationOptions<RunningProject, { projectId: number }>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ projectId }) =>
      axios.post(`/start_project/${projectId}/`).then(unwrap),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['running'] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useResumeProject(
  options?: MutationOptions<RunningProject, { runningProjectId: number }>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ runningProjectId }) =>
      axios.patch(`/start_paused_project/${runningProjectId}/`).then(unwrap),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['running'] });
      options?.onSuccess?.(...args);
    },
  });
}

type EndProjectRequest = {
  runningProjectId: number;
  comment?: string;
};

export function useEndProject(
  options?: MutationOptions<void, EndProjectRequest>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ runningProjectId, comment }) =>
      axios
        .post(`/end_project/${runningProjectId}/`, { comment: comment ?? '' })
        .then(unwrap),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['running'] });
      options?.onSuccess?.(...args);
    },
  });
}
